import { ArrowDownToLine, Loader2, RefreshCw } from "lucide-react";
import type { TemplateStudioType } from "@shared/schema";
import type { CreativeAdRow, TopPerformerMetric } from "@shared/creative-performance";
import { creativeAdDisplayUrls, DEFAULT_MIN_SPEND, topPerformerValue } from "@shared/creative-performance";
import { formatPerformanceMetricLabel } from "@/lib/concept-board-reference-selection";
import { metricLabel, type ImportStatus, type LibraryImage, type SuggestionState, type TopPerformerWindow } from "@/lib/template-studio-library";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PerformerTypeSuggestions } from "@/components/template-studio-library/type-suggestions";
import { ReferenceImage } from "@/components/template-studio-library/reference-image";
import { ReferencePagination, type useReferencePagination } from "@/components/template-studio-library/reference-pagination";

function TopPerformerThumbnail({ ad }: { ad: CreativeAdRow }) {
  const urls = creativeAdDisplayUrls(ad);
  return <ReferenceImage sources={urls} alt={`Creative preview for ${ad.adName}`} testId={`performer-preview-${ad.adId}`} />;
}

type Props = {
  typesLoading: boolean;
  typesError: boolean;
  available: TemplateStudioType[];
  refetchTypes: () => void;
  membershipsLoading: boolean;
  membershipsError: boolean;
  refetchMemberships: () => void;
  configLoading: boolean;
  configError: boolean;
  performanceEnabled: boolean;
  refetchConfig: () => void;
  adsLoading: boolean;
  adsError: boolean;
  refetchAds: () => void;
  topPerformers: CreativeAdRow[];
  pagination: ReturnType<typeof useReferencePagination<CreativeAdRow>>;
  metric: TopPerformerMetric;
  since: string;
  until: string;
  windowDays: TopPerformerWindow;
  onWindowDaysChange: (days: TopPerformerWindow) => void;
  membershipByAd: ReadonlyMap<string, LibraryImage>;
  selectedAds: string[];
  onSelectionChange: (adId: string, checked: boolean) => void;
  cardTypeIds: Record<string, string>;
  onTypeChange: (adId: string, typeId: string) => void;
  performerSuggestions: Record<string, SuggestionState | undefined>;
  onSuggestType: (ad: CreativeAdRow) => void;
  onConfirmSuggestion: (adId: string, typeId: string) => void;
  importStatuses: Record<string, ImportStatus>;
  importSummary: string;
  importsBusy: boolean;
  onAddSelected: () => void;
};

export function TopPerformersPanel(props: Props) {
  const {
    typesLoading, typesError, available, refetchTypes,
    membershipsLoading, membershipsError, refetchMemberships,
    configLoading, configError, performanceEnabled, refetchConfig,
    adsLoading, adsError, refetchAds, topPerformers, metric, since, until,
    windowDays, onWindowDaysChange, membershipByAd, selectedAds, onSelectionChange,
    cardTypeIds, onTypeChange, performerSuggestions, onSuggestType, onConfirmSuggestion,
    importStatuses, importSummary, importsBusy, onAddSelected, pagination,
  } = props;
  const hasMissingTypes = selectedAds.some((id) => !cardTypeIds[id]);

  return <section className="rounded-xl border border-violet-100 bg-white p-3 shadow-[0_8px_28px_-24px_rgba(50,34,90,.35)] sm:p-5" aria-labelledby="performers-heading">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 id="performers-heading" className="text-lg font-bold text-slate-900">Top performers</h2>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-600 sm:text-sm">Choose eligible static ads to add to this brand's reference library. Each card needs a board type before import.</p>
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/50 px-2.5 py-1.5">
        <Label htmlFor="performer-window" className="text-xs font-semibold text-slate-600">Window</Label>
        <select id="performer-window" className="h-8 rounded-md border border-violet-200 bg-white px-2 text-xs text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500" value={windowDays}
          onChange={(event) => onWindowDaysChange(Number(event.target.value) as TopPerformerWindow)}>
          <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
        </select>
      </div>
    </div>
    <p className="mt-3 text-[11px] text-slate-500">Ranked by {metricLabel(metric)}. Minimum spend ${DEFAULT_MIN_SPEND}; only signed, eligible static image ads are shown.</p>
    {!typesError && available.length > 0 && <p className="mt-2 text-[11px] text-slate-500">Suggestions use credits only when you explicitly choose Suggest type; nothing is assigned automatically.</p>}

    {typesLoading ? <div role="status" className="mt-5 h-24 animate-pulse rounded-md bg-muted" aria-label="Loading board types" />
      : typesError ? <div role="alert" className="mt-5 rounded-md border p-4">
        <p>Board types are unavailable, so performers cannot be assigned.</p>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={refetchTypes}>Retry board types</Button>
      </div> : !available.length ? <p role="status" className="mt-5 rounded-md border p-4 text-sm text-muted-foreground">No enabled board types are available. Enable a type before importing performers.</p>
        : membershipsLoading ? <div role="status" className="mt-5 h-24 animate-pulse rounded-md bg-muted" aria-label="Checking library membership" />
          : membershipsError ? <div role="alert" className="mt-5 rounded-md border p-4">
            <p>Library membership could not be checked. Retry before importing to avoid duplicate references.</p>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={refetchMemberships}>Retry membership</Button>
          </div> : configLoading ? <div role="status" className="mt-5 h-24 animate-pulse rounded-md bg-muted" aria-label="Loading performance settings" />
            : configError ? <div role="alert" className="mt-5 rounded-md border p-4">
              <p>Creative Performance settings are unavailable.</p>
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={refetchConfig}>Retry settings</Button>
            </div> : !performanceEnabled ? <p role="status" className="mt-5 rounded-md border p-4 text-sm text-muted-foreground">Creative Performance is not enabled for this brand.</p>
               : adsLoading ? <div role="status" className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                 {[0, 1, 2].map((item) => <div key={item} className="aspect-[.9] animate-pulse rounded-xl bg-violet-50" aria-label="Loading top performers" />)}
              </div> : adsError ? <div role="alert" className="mt-5 rounded-md border p-4">
                <p>Top performers are unavailable right now.</p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={refetchAds}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
              </div> : !topPerformers.length ? <p role="status" className="mt-5 rounded-md border p-6 text-center text-sm text-muted-foreground">No eligible static image ads met the ranking requirements in this window.</p>
                : <>
                   <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-violet-100 bg-violet-50/60 p-3">
                     <span className="text-xs font-medium text-slate-600">{selectedAds.length} selected · {since} to {until}</span>
                     <Button type="button" size="sm" className="bg-violet-700 text-white hover:bg-violet-800" onClick={onAddSelected} disabled={!selectedAds.length || hasMissingTypes || importsBusy} data-testid="add-selected-performers">
                      {importsBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowDownToLine className="mr-2 h-4 w-4" />}
                      Add selected to library
                    </Button>
                  </div>
                  {importSummary && <p role="status" className="text-sm">{importSummary}</p>}
                  {selectedAds.length > 0 && hasMissingTypes && <p role="alert" className="text-sm text-muted-foreground">Choose a board type for each selected ad before adding.</p>}
                   <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {pagination.items.map((ad) => {
                      const membership = membershipByAd.get(ad.adId);
                      const state = importStatuses[ad.adId];
                      const selected = selectedAds.includes(ad.adId);
                      const typeId = cardTypeIds[ad.adId] || "";
                      const value = topPerformerValue(ad, metric);
                       return <Card key={ad.adId} className="overflow-hidden rounded-xl border-violet-100 bg-white shadow-[0_5px_18px_-16px_rgba(50,34,90,.45)]" data-testid={`performer-card-${ad.adId}`}>
                        <div className="relative">
                          <TopPerformerThumbnail ad={ad} />
                           <label className="absolute left-2 top-2 flex cursor-pointer items-center gap-1.5 rounded-md border border-violet-100 bg-white/95 px-2 py-1.5 text-xs font-semibold text-slate-800 shadow-sm">
                            <Checkbox checked={selected} aria-label={`Select ${ad.adName} for library import`} data-testid={`select-performer-${ad.adId}`}
                              onCheckedChange={(checked) => onSelectionChange(ad.adId, checked === true)} />
                            Select
                          </label>
                        </div>
                         <CardContent className="space-y-2.5 p-3">
                          <div>
                             <h3 className="line-clamp-2 text-sm font-semibold text-slate-900" title={ad.adName}>{ad.adName}</h3>
                             <p className="mt-1 text-[11px] text-slate-500">{formatPerformanceMetricLabel({
                              metric, value, spend: ad.spend, costPer: value > 0 ? ad.spend / value : null,
                            })}</p>
                          </div>
                           {membership && <p className="rounded-md bg-violet-50 px-2.5 py-1.5 text-[11px] font-medium text-violet-800" data-testid={`membership-${ad.adId}`}>
                            In library{membership.typeIds.length ? ` · ${membership.typeIds.length} board type${membership.typeIds.length === 1 ? "" : "s"}` : " · unassigned"}
                          </p>}
                           {typeId && <div className="flex flex-wrap gap-1.5" aria-label="Selected board type">
                             {available.filter((type) => type.id === typeId).map((type) => <span key={type.id} className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-800">{type.name}</span>)}
                           </div>}
                           <details className="group rounded-lg border border-violet-100 bg-[#fcfbff]">
                             <summary data-testid={`performer-controls-${ad.adId}`} className="flex cursor-pointer list-none items-center justify-between gap-2 px-2.5 py-2 text-xs font-semibold text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 [&::-webkit-details-marker]:hidden">
                               <span>{typeId ? "Change board type" : "Choose board type"}</span><span aria-hidden="true" className="text-violet-500 transition-transform group-open:rotate-45">+</span>
                             </summary>
                             <div className="space-y-2 border-t border-violet-100 p-2.5">
                               <div className="space-y-1">
                                 <Label htmlFor={`performer-type-${ad.adId}`} className="text-[11px] text-slate-600">Assign one type to this ad</Label>
                                 <select id={`performer-type-${ad.adId}`} aria-label={`Board type for ${ad.adName}`} className="h-9 w-full rounded-md border border-violet-200 bg-white px-2.5 text-xs"
                                   value={typeId} onChange={(event) => onTypeChange(ad.adId, event.target.value)} data-testid={`performer-type-${ad.adId}`}>
                                   <option value="">Choose a board type</option>
                                   {available.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                                 </select>
                               </div>
                               {!typeId && <PerformerTypeSuggestions adId={ad.adId} available={available} state={performerSuggestions[ad.adId]}
                                 onSuggest={() => onSuggestType(ad)}
                                 onConfirm={(suggestedTypeId) => onConfirmSuggestion(ad.adId, suggestedTypeId)} />}
                             </div>
                           </details>
                          {state?.state === "pending" && <p role="status" className="text-xs text-muted-foreground">Adding to library...</p>}
                          {state?.state === "success" && <p role="status" className="text-xs text-primary">Added or updated in library.</p>}
                          {state?.state === "error" && <p role="alert" className="text-xs text-destructive">{state.error}</p>}
                        </CardContent>
                      </Card>;
                    })}
                  </div>
                  <ReferencePagination {...pagination} source="Top performers" />
                </>}
  </section>;
}